import { useEffect, useRef } from 'react';
import { de } from '@/i18n/de';
import { useCountdown, useSmoothFraction } from '@/lib/clock';
import { haptics } from '@/lib/haptics';
import { sound } from '@/lib/sound';

/** Ab hier färbt sich der Ring, pulsiert er — und meldet er sich einmal. */
const URGENT_SECONDS = 10;

interface TimerProps {
  endsAt: number | null;
  totalSeconds: number;
  size?: number;
  /** Was hier abläuft — ohne den Namen meldet der Screenreader nur „Timer". */
  label: string;
}

/**
 * Großer Ring-Timer (GDD §12, §38). Wechselt unter 10 Sekunden Farbe und
 * pulsiert dezent — rein optisch, ohne die Fraktionsfarben zu verwenden.
 *
 * Ring und Ziffern haben bewusst zwei Taktgeber: der Ring läuft per Frame
 * kontinuierlich ab, die Ziffern springen einmal pro Sekunde. Vorher lief
 * beides im Sekundentakt, und eine 0,3-s-Transition musste die Sprünge
 * kaschieren.
 */
export function Timer({ endsAt, totalSeconds, size = 220, label }: TimerProps) {
  const remaining = useCountdown(endsAt);
  const fraction = useSmoothFraction(endsAt, totalSeconds);
  const urgent = remaining <= URGENT_SECONDS && remaining > 0;

  useUrgentCue(endsAt, totalSeconds, remaining);

  // Ein 10px-Ring erdrückt die kleinen Varianten (Rollenphase, Stichwahl-Fuß);
  // die Geometrie der großen Timer bleibt unverändert, weil `strokeWidth + 2`
  // dort genau die bisherigen 12px ergibt.
  const strokeWidth = size < 100 ? 6 : 10;
  const radius = size / 2 - (strokeWidth + 2);
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - fraction);

  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const display = totalSeconds >= 60 ? `${minutes}:${String(secs).padStart(2, '0')}` : `${remaining}`;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${urgent ? 'animate-pulse-soft' : ''}`}
      style={{ width: size, height: size }}
      role="timer"
      // Der Restwert steht im Namen, nicht als vorgelesener Inhalt: `aria-live`
      // bliebe sonst dabei, jede Sekunde dazwischenzureden. Wer den Timer
      // ansteuert, hört den aktuellen Stand — ungefragt niemand.
      aria-label={de.common.timerLabel(label, remaining)}
      aria-live="off"
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-surface-2)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={urgent ? 'var(--color-mafia)' : 'var(--color-accent)'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke 0.3s ease' }}
        />
      </svg>
      <span
        aria-hidden="true"
        className="absolute font-display font-bold tabular-nums text-fg"
        // Die Untergrenze muss selbst mitschrumpfen, sonst quellen die Ziffern
        // aus den kleinen Ringen heraus.
        style={{ fontSize: `clamp(min(2.5rem, ${size * 0.34}px), ${size * 0.22}px, 5.5rem)` }}
      >
        {display}
      </span>
    </div>
  );
}

/**
 * Einmaliges Signal, wenn es knapp wird.
 *
 * Das Pulsieren des Rings hilft nur dem, der hinsieht — während der Diskussion
 * liegt das Handy aber auf dem Tisch. Ton und Vibration erreichen den Tisch
 * auch dann.
 *
 * Der Merker hängt an `endsAt`, nicht an einem Boolean: sonst schwiege die
 * nächste Phase, weil das Signal schon „verbraucht" wäre. Kurze Phasen
 * (Tie-Ansage: 6 s, Stichwahl-Minimum: 10 s) sind ausgenommen — dort wäre die
 * Warnung schon beim Erscheinen fällig und damit keine Warnung mehr.
 */
function useUrgentCue(endsAt: number | null, totalSeconds: number, remaining: number): void {
  const signalledFor = useRef<number | null>(null);

  useEffect(() => {
    if (endsAt === null || totalSeconds <= URGENT_SECONDS) return;
    if (remaining > URGENT_SECONDS || remaining <= 0) return;
    if (signalledFor.current === endsAt) return;

    signalledFor.current = endsAt;
    sound.tick();
    haptics.tick();
  }, [endsAt, totalSeconds, remaining]);
}
